import { useMemo, useState } from 'react'
import {
  Box,
  ButtonBase,
  FormControlLabel,
  Stack,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { Card, Modal, Skeleton, Switch } from '@repo/ui'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useSeoRankings, useSeoSerp } from 'hooks/api'
import type { SeoCompetitor, SeoStatus } from 'services'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, dateTime, useTone } from '../shared'
import { fmt, fmtPos, PositionChip, scheduleLabel, Sparkline } from './seoModel'
import { CompetitorMenu, KIND_LABEL, ReviewChip, useCompetitorLookup } from './seoReview'
import { TagFilters, useTagFilters } from './seoTagFilters'
import { CompetitorLink } from '../../../CompetitorResearchPage/CompetitorLink'

type Filter = 'all' | 'gaps'
const FILTERS: readonly ViewToggleOption<Filter>[] = [
  { value: 'all', label: 'All tracked terms' },
  { value: 'gaps', label: 'Gaps only' },
]

const shortDomain = (d: string) => d.replace(/\.(com|io|app|xyz|inc|co|net|org)$/, '')

/**
 * One term's newest result page, every result labelled for review: a competitor (and its
 * status), an excluded platform, or a site nobody has looked at yet. The kebab on each
 * row is the same "Add to competitors / Reject / Remove" menu as everywhere else.
 */
const TopResultsModal = ({
  market,
  keywordId,
  keyword,
  onClose,
}: {
  market: string
  keywordId: string | null
  keyword: string
  onClose: () => void
}) => {
  const { data, isLoading, error } = useSeoSerp(market, keywordId)
  return (
    <Modal
      open={!!keywordId}
      onClose={onClose}
      title={`Top results: ${keyword}`}
      description={
        data?.checkedAt
          ? `Google ${market.toUpperCase()}, organic results only, checked ${dateTime(data.checkedAt)}${
              data.provider ? ` by ${data.provider.id}` : ''
            }.`
          : 'Organic results from the newest completed check.'
      }
    >
      {isLoading ? (
        <Skeleton height={14} lines={10} />
      ) : error || !data ? (
        <SectionNote>Could not load the result page: {(error as Error | null)?.message}</SectionNote>
      ) : data.results.length === 0 ? (
        <SectionNote>No completed check for this term yet.</SectionNote>
      ) : (
        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell align="right">#</HeadCell>
              <HeadCell>Site</HeadCell>
              <HeadCell>Status</HeadCell>
              <HeadCell align="right"> </HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.results.slice(0, 20).map((r) => (
              <TableRow key={`${r.position}-${r.domain}`}>
                <Cell align="right">{r.position}</Cell>
                <Cell strong={r.vera || r.competitor?.status === 'approved'}>
                  {/* Fixed width + ellipsis: a modal never scrolls sideways (§74). */}
                  <Box sx={{ width: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.domain}
                    {r.title ? (
                      <Typography
                        component="div"
                        variant="body3"
                        color="text.secondary"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 400 }}
                      >
                        {r.title}
                      </Typography>
                    ) : null}
                  </Box>
                </Cell>
                <Cell>
                  <Box component="span" sx={{ whiteSpace: 'normal' }}>
                  {r.vera
                    ? 'Vera'
                    : r.competitor
                    ? r.competitor.status === 'approved'
                      ? `${KIND_LABEL[r.competitor.kind]} competitor`
                      : null
                    : r.excluded
                    ? 'Generic platform'
                    : '—'}
                  <ReviewChip status={r.competitor?.status} />
                  </Box>
                </Cell>
                <Cell align="right">
                  {r.vera || r.excluded ? null : (
                    <CompetitorMenu
                      domain={r.competitor?.domain ?? r.site}
                      status={r.competitor?.status ?? null}
                    />
                  )}
                </Cell>
              </TableRow>
            ))}
          </TableBody>
        </StatTable>
      )}
    </Modal>
  )
}

/**
 * Tracked terms × Vera and each APPROVED competitor, from Vera's own live SERP checks.
 * The source and method are stated on the card: these are one location's organic
 * results on one day, not an average, and a rank tool's view of Google can disagree with
 * a person's. Adjacent competitors are an optional column group, off by default.
 */
export const SeoRankingsCard = ({
  market,
  competitors,
  status,
}: {
  market: string
  competitors: SeoCompetitor[]
  status: SeoStatus
}) => {
  const tone = useTone()
  const tags = useTagFilters()
  const tracking = status.tracking.markets.find((m) => m.market === market)
  const [adjacent, setAdjacent] = useState(false)
  const { data, isLoading, error } = useSeoRankings(market, adjacent)
  const [filter, setFilter] = useState<Filter>('all')
  const [serpFor, setSerpFor] = useState<{ id: string; keyword: string } | null>(null)
  const lookup = useCompetitorLookup(competitors)

  const rows = useMemo(
    () =>
      (data?.rows ?? [])
        .filter((r) => (filter === 'gaps' ? r.gap : true))
        .filter((r) => tags.matches(r)),
    [data, filter, tags.state],
  )
  const gapCount = (data?.rows ?? []).filter((r) => r.gap).length

  return (
    <Card
      title="Rankings"
      action={
        <Stack direction="row" spacing={4} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <FormControlLabel
            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            control={
              <Switch
                checked={adjacent}
                onChange={(_: unknown, checked: boolean) => setAdjacent(checked)}
                inputProps={{ 'aria-label': 'Show adjacent competitors' }}
              />
            }
            label={`Adjacent${data ? ` (${data.adjacentAvailable})` : ''}`}
          />
          <ViewToggle
            options={FILTERS}
            value={filter}
            onChange={setFilter}
            ariaLabel="Rankings filter"
          />
        </Stack>
      }
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        {isLoading ? (
          <Skeleton height={14} lines={10} />
        ) : error || !data ? (
          <SectionNote>Could not load rankings: {(error as Error | null)?.message}</SectionNote>
        ) : (
          <>
            <Notice severity="info">
              Source: {data.provider.id}, {data.provider.method.replace(/\.$/, '')}.{' '}
              {data.market
                ? `Google results for ${data.market.serpLocationName} (${data.market.serpLanguageCode}), checked ${scheduleLabel(status.schedule, data.market.rankCadenceDays)}. `
                : ''}
              {data.depthNote}{' '}
              {data.verifiedCountry
                ? `Latest result page confirmed as Google ${data.verifiedCountry}.`
                : 'Country not yet confirmed from a result page.'}
            </Notice>
            {tracking ? (
              <SectionNote>
                <strong>Tracked by rule</strong>
                <InfoTooltip term="seo-tracking-rule" />: {fmt(tracking.rule)} terms (+{fmt(tracking.ownIt)}{' '}
                own-it, +{fmt(tracking.manual)} in this market&rsquo;s set), checked every{' '}
                {tracking.cadenceDays} days ({tracking.checksPerMonth}/month) at{' '}
                {status.tracking.creditsPerCheck} credits a check: about {fmt(tracking.estimatedCredits)}{' '}
                credits a month here, {fmt(status.tracking.totalEstimatedCredits)} across markets (cap{' '}
                {fmt(status.tracking.creditCap)} before keyword metrics). Rule: buyer fit &ge;{' '}
                {status.tracking.rule.minBuyerFit} and &ge; {status.tracking.rule.minVolume} monthly
                searches, or own-it.
              </SectionNote>
            ) : null}
            <TagFilters taxonomy={status.taxonomy} state={tags.state} onChange={tags.setState} />
            <SectionNote>
              {fmt(data.rows.length)} tracked terms, {fmt(gapCount)} gaps
              <InfoTooltip term="seo-rank-gap" />. Last check{' '}
              {data.lastCheckAt ? dateTime(data.lastCheckAt) : 'never'}
              {data.pendingChecks ? `, ${data.pendingChecks} still processing` : ''}.
              &ldquo;GSC&rdquo; is vera.inc&rsquo;s own impression-weighted average position over 28
              days, which is a different measure from a single check. Columns are approved
              competitors{data.adjacentIncluded ? ', adjacent ones included' : ''}; open a term to see
              its whole result page.
              {data.market?.mode === 'competitors_only'
                ? ' This market tracks competitors only: Vera has no local pages yet, so its column is expected to be empty.'
                : ''}
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Keyword</HeadCell>
                  <HeadCell align="right">Volume</HeadCell>
                  <HeadCell align="right">
                    GSC
                    <InfoTooltip
                      term="gsc-average-position"
                      text="vera.inc's impression-weighted average Google position over the last 28 days. Source: Google Search Console, synced nightly, 2–3 day lag."
                    />
                  </HeadCell>
                  {data.domains.map((d) => (
                    <HeadCell key={d.domain} align="right">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        component="span"
                        sx={{ color: d.kind === 'adjacent' ? 'text.disabled' : undefined }}
                      >
                        {d.kind === 'vera' ? (
                          'Vera'
                        ) : (
                          <InfoTooltip text={`${d.domain} · ${KIND_LABEL[d.kind]}`}>
                            {/* VRG-744 — a competitor's name links to its profile on Competitor research. */}
                            <span>
                              <CompetitorLink domain={d.domain}>{d.name ?? shortDomain(d.domain)}</CompetitorLink>
                            </span>
                          </InfoTooltip>
                        )}
                        {d.kind === 'vera' ? null : (
                          <CompetitorMenu domain={d.domain} status="approved" />
                        )}
                      </Stack>
                    </HeadCell>
                  ))}
                  <HeadCell>Vera trend</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <EmptyRow
                    colSpan={4 + data.domains.length}
                    text={
                      data.rows.length === 0
                        ? 'No checks yet for this market. The rankings job runs on its cadence, or a platform admin can run it from Jobs and spend.'
                        : tags.active
                        ? 'No tracked terms carry these tags.'
                        : 'No gaps: wherever a competitor is on page one, so is Vera.'
                    }
                  />
                ) : (
                  rows.map((r) => {
                    const vera = r.cells.find(
                      (c) => c.domain === data.domains.find((d) => d.kind === 'vera')?.domain,
                    )
                    return (
                      <TableRow key={r.keywordId}>
                        <Cell strong>
                          {/* A span around the button: a disabled ButtonBase fires no pointer
                              events, so its tooltip could never open on "Not checked yet". */}
                          <InfoTooltip
                            text={r.checkedAt ? 'Show the top results for this term' : 'Not checked yet'}
                          >
                            <span>
                              <ButtonBase
                                disabled={!r.checkedAt}
                                onClick={() => setSerpFor({ id: r.keywordId, keyword: r.keyword })}
                                sx={{
                                  font: 'inherit',
                                  color: 'inherit',
                                  textAlign: 'left',
                                  borderRadius: 1,
                                  '&:hover': r.checkedAt ? { textDecoration: 'underline' } : undefined,
                                }}
                              >
                                {r.keyword}
                              </ButtonBase>
                            </span>
                          </InfoTooltip>
                          {r.gap ? (
                            <InfoTooltip term="seo-rank-gap">
                              <Typography
                                component="span"
                                variant="body3"
                                sx={{ ml: 1, color: tone('caution').color, cursor: 'help' }}
                              >
                                gap
                              </Typography>
                            </InfoTooltip>
                          ) : null}
                          {r.trackReason && r.trackReason !== 'rule' ? (
                            <InfoTooltip
                              term={r.trackReason === 'own-it' ? 'seo-own-it' : 'seo-tracking-rule'}
                              text={r.trackReason === 'own-it' ? 'Tracked because it is own-it' : 'In this market’s explicit set'}
                            >
                              <Typography
                                component="span"
                                variant="body3"
                                color="text.secondary"
                                sx={{ ml: 1, fontSize: 11, cursor: 'help' }}
                              >
                                {r.trackReason === 'own-it' ? 'own-it' : 'set'}
                              </Typography>
                            </InfoTooltip>
                          ) : null}
                        </Cell>
                        <Cell align="right">{fmt(r.volume)}</Cell>
                        <Cell align="right">{fmtPos(r.veraGscPosition)}</Cell>
                        {data.domains.map((d) => {
                          const c = r.cells.find((x) => x.domain === d.domain)
                          return (
                            <Cell key={d.domain} align="right">
                              <PositionChip
                                position={c?.position ?? null}
                                highlight={d.kind === 'vera'}
                              />
                            </Cell>
                          )
                        })}
                        <Cell>
                          <Sparkline
                            values={vera?.history ?? []}
                            invert
                            label={`Vera rank for ${r.keyword}`}
                          />
                        </Cell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </StatTable>
            {data.discovered.length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Also on page one, not a competitor
                </Typography>
                <SectionNote>
                  Sites in the top 10 of the latest checks that are not approved competitors.
                  Generic platforms and rejected sites are left out. A site in the top 10 for{' '}
                  three or more terms is proposed automatically; add or reject any of them here.
                </SectionNote>
                <StatTable>
                  <TableHead>
                    <TableRow>
                      <HeadCell>Site</HeadCell>
                      <HeadCell align="right">Terms on page one</HeadCell>
                      <HeadCell>Status</HeadCell>
                      <HeadCell align="right"> </HeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.discovered.slice(0, 15).map((d) => {
                      const known = lookup(d.domain)
                      return (
                        <TableRow key={d.domain}>
                          <Cell strong>{d.domain}</Cell>
                          <Cell align="right">{fmt(d.top10)}</Cell>
                          <Cell>
                            {d.status ? <ReviewChip status={d.status} /> : '—'}
                          </Cell>
                          <Cell align="right">
                            <CompetitorMenu
                              domain={known?.domain ?? d.domain}
                              status={known?.status ?? d.status}
                            />
                          </Cell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </StatTable>
              </Box>
            ) : null}
          </>
        )}
      </Stack>
      <TopResultsModal
        market={market}
        keywordId={serpFor?.id ?? null}
        keyword={serpFor?.keyword ?? ''}
        onClose={() => setSerpFor(null)}
      />
    </Card>
  )
}
